import {useRef, useState} from 'react';
import {surveyInstance} from 'screens/InsomniaSurvey/InsomniaSurvey';
import Analytics from 'utils/Analytics';
import {readSeverityFromUrl} from 'surveys/osa_funnel/survey/severity';
import {readReturnToFromUrl} from 'surveys/osa_funnel/survey/returnTo';
import {useGlp1Modality} from 'surveys/osa_funnel/hooks/useGlp1Modality';
import {PrimerSlide, primerStyles} from './PrimerShell';

type FilterValue = 'yes' | 'maybe' | 'no';

type FilterOption = {
  emoji: string;
  label: string;
  value: FilterValue;
};

const FILTER_OPTIONS: FilterOption[] = [
  {emoji: '💪', label: 'Yes, move me forward', value: 'yes'},
  {emoji: '🤔', label: 'Yes, but I have a few questions', value: 'maybe'},
  {emoji: '🤨', label: 'No, not now', value: 'no'},
];

/** Primer slide E — filter: "Do you want to move forward with RISE
 *  plus medication?" Writes `primer_filter` to survey data, then
 *  advances. yes/maybe → paywall; no → dismiss survey via
 *  osa_dismiss calculatedValue. */
export const Glp1PrimerFilter = () => {
  const modality = useGlp1Modality();
  const [selection, setSelection] = useState<FilterOption | null>(null);
  // Shuffle non-exit options once per mount so we don't bias selections.
  // `no` (the exit) is always pinned last.
  const optionsRef = useRef<FilterOption[]>(
    (() => {
      const exits = FILTER_OPTIONS.filter(o => o.value === 'no');
      const nonExits = [...FILTER_OPTIONS.filter(o => o.value !== 'no')];
      for (let i = nonExits.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [nonExits[i], nonExits[j]] = [nonExits[j], nonExits[i]];
      }
      return [...nonExits, ...exits];
    })(),
  );

  const handleSubmit = () => {
    if (!selection) return;
    Analytics.track('GLP-1 Funnel Primer Filter Submitted', {
      value: selection.value,
      isExit: selection.value === 'no',
      severity: readSeverityFromUrl(),
      variant: modality,
    });
    surveyInstance.setValue('primer_filter', selection.value);

    // "No, not now" is the hard exit — push the user out of the GLP-1
    // funnel entirely. Send them to the main onboarding entry so they
    // can keep going with the RISE app (no medication path).
    if (selection.value === 'no') {
      window.location.href = readReturnToFromUrl();
      return;
    }

    if (surveyInstance.isLastPage) {
      surveyInstance.completeLastPage();
    } else {
      surveyInstance.nextPage();
    }
  };

  return (
    <PrimerSlide
      letter="E"
      headline="Do you want to move forward with RISE plus medication?"
      accentPhrase="Do you want to move forward"
      subhead="Your care team is ready when you are. If not, you can continue with the RISE App only."
      ctaLabel="Next"
      ctaDisabled={!selection}
      onCta={handleSubmit}
    >
      <div className={primerStyles.filterList}>
        {optionsRef.current.map(option => {
          const selected = selection?.value === option.value;
          return (
            <button
              key={option.value}
              type="button"
              className={`${primerStyles.filterRow} ${selected ? primerStyles.filterRowSelected : ''}`}
              onClick={() => setSelection(option)}
              aria-pressed={selected}
            >
              <span className={primerStyles.filterEmoji} aria-hidden="true">
                {option.emoji}
              </span>
              <span className={primerStyles.filterLabel}>{option.label}</span>
              <span className={primerStyles.radio} aria-hidden="true">
                {selected ? <span className={primerStyles.radioDot} /> : null}
              </span>
            </button>
          );
        })}
      </div>
    </PrimerSlide>
  );
};

export default Glp1PrimerFilter;
